'use client';

import React from 'react';
import { StepOneArt, StepTwoArt, StepThreeArt } from './StepArt';

/**
 * Figma desktop 303:3369 / mobile 303:2975.
 *
 * Steps 1 and 3's illustrations are single final photos (StepArt.tsx); step 2 is still a
 * layered composition. All three now share the same card structure/sizing.
 */
const STEPS = [
  {
    n: 1,
    title: 'Add your friends and family',
    // Desktop forces this exact line break (mobile keeps the plain string, wrapping
    // naturally at whatever width the device gives it — see the h3 below).
    titleLines: ['Add your friends', 'and family'],
    bg: 'var(--hp-step-1-bg)',
    Art: StepOneArt,
  },
  {
    n: 2,
    title: 'Pick a theme or write your story',
    titleLines: ['Pick a theme or write', 'your story'],
    bg: 'var(--hp-step-2-bg)',
    Art: StepTwoArt,
  },
  {
    n: 3,
    title: 'Get a stunning printed comics',
    titleLines: ['Get a stunning', 'printed comics'],
    bg: 'var(--hp-step-3-bg)',
    Art: StepThreeArt,
  },
] as const;

export default function HowItWorks() {
  return (
    // Bottom padding is trimmed so the boundary with Community isn't a full double
    // section-y gap. Mobile matches gap-6 (24px, same as the step-card stacking gap).
    // Desktop resets to 0 here and instead applies a fixed pb-[40px] on the steps row
    // below.
    <section className="w-full pt-[var(--hp-section-y)] pb-6 dt:pb-0">
      <div className="hp-container">
        <h2
          className="text-center font-bold tracking-[-0.454px] text-[var(--hp-ink)]"
          style={{ fontSize: 'var(--hp-h2)', lineHeight: 1.35 }}
        >
          How it works
        </h2>

        {/* Mobile stacks the steps; desktop lays them out as three equal columns. */}
        <div className="mt-8 flex flex-col items-center gap-6 dt:mt-10 dt:flex-row dt:items-start dt:gap-[2.4%] dt:pb-[40px]">
          {STEPS.map(({ n, title, titleLines, bg, Art }) => (
            <article
              key={n}
              className="w-full overflow-hidden rounded-[var(--hp-radius)] dt:flex-1"
            >
              {/* Fixed 142px on desktop (24px padding-top + the 38px number + a 12px
                  gap + up to two 24px/140% title lines) so all three cards' top
                  sections line up regardless of title length; mobile stays auto-height. */}
              <div
                className="flex flex-col items-center gap-3 px-0 pt-[24px] dt:px-3 dt:h-[142px]"
                style={{ backgroundColor: bg }}
              >
                <span
                  className="flex size-[38px] shrink-0 items-center justify-center rounded-full bg-white text-center text-[20px] font-semibold text-[var(--hp-ink-soft)] drop-shadow-[0_4px_3.5px_rgba(0,0,0,0.05)]"
                  aria-hidden="true"
                >
                  {n}
                </span>
                {/* w-full: the parent is `items-center`, so a flex item without an
                    explicit width shrinks to its own fit-content instead of using the
                    card's full width — cutting off the room text needs to fit on one
                    line. Mobile shows the plain string (wraps naturally, one line
                    whenever the full-width card allows it). Desktop forces the exact
                    final-design line break via titleLines and an explicit <br/>, so no
                    max-w is needed to induce wrapping — that only introduced a width/
                    centering mismatch against the reference. */}
                <h3
                  className="w-full text-center font-semibold text-[var(--hp-ink-soft)]"
                  style={{ fontSize: 'var(--hp-step-title)', lineHeight: 1.4 }}
                >
                  <span className="dt:hidden">{title}</span>
                  <span className="hidden dt:inline">
                    {titleLines[0]}{' '}
                    <br />
                    {titleLines[1]}
                  </span>
                </h3>
              </div>

              {/* Fixed 290px on desktop, matching the top section's fixed height so
                  every card's total height lines up. */}
              <div className="hp-step-art" style={{ backgroundColor: bg }}>
                <Art />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
